import { Images } from "../../../Images";
import TileMinedLogic from "../../../logic/TileMinedLogic";
import { Settings } from "../../../Settings";
import OrbitSkillRenderer from "../../../view/canvas/OrbitSkillRenderer";
import PartyCharacter from "../../character/PartyCharacter";
import Crew from "../../crew/Crew";
import Totals from "../../crew/Totals";
import WorldGrid from "../../grid/WorldGrid";
import BigNum from "../../math/BigNum";
import GervaisAnimatedSpritesheet from "../../sprite/GervaisAnimatedSpritesheet";
import NumberValue from "../../value/NumberValue";
import AddMinerSkillNode from "../common/AddMInerSkillNode";
import CoolDownSkillNode from "../common/CoolDownSkillNode";
import DurationSkillNode from "../common/DurationSkillNode";
import NumberValueSkillNode from "../common/NumberValueSkillNode";
import { SkillIds } from "../SkillIds";
import SkillType from "../SkillType";
import Orbit from "./Orbit";

export default class OrbitSkill extends SkillType {
    /**
     * @param {WorldGrid} worldGrid 
     * @param {Totals} totals
     * @param {TileMinedLogic} tileMinedLogic 
     * @param {Crew} crew
     * @param {GervaisAnimatedSpritesheet} spellFxSpritesheet
     */
    constructor(worldGrid, totals, tileMinedLogic, crew, spellFxSpritesheet) {
        super(SkillIds.ORBIT);

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;

        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;
        /**
         * @constant
         * @type {Crew}
         */
        this.crew = crew;

        /**
         * @constant
         * @type {NumberValue}
         */
        this.orbMaxUpgrades = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.orbitRangeUpgrades = new NumberValue(null, 0);

        /**
         * @constant
         * @type {Orbit[]}
         */
        this.orbitArray = []; 

        /**
         * @constant
         * @type {Object}
         */
        this.orbitByCharacter = {};

        /**
         * @constant
         * @type {SkillRenderer}
         */
        this.renderer = new OrbitSkillRenderer(this.orbitArray, spellFxSpritesheet);

        let cost = 1;

        const addMinerNode1 = this.addNode(new AddMinerSkillNode('addOrbitMiner1', '+1 Orbit Miner', 'Orbiting Damage', 
            totals, Images.skill.orbit, new BigNum(cost + Settings.skill.costs.addOrbit1), this, crew, SkillIds.ORBIT
        ));
    
        ////////////////////////////
        // Cost Tier 2
        ////////////////////////////

        cost++;

        const orbitRange1 = this.addNode(new NumberValueSkillNode('orbitRange1', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus1 = this.addNode(new NumberValueSkillNode('orbBonus1', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus1 = this.addNode(new CoolDownSkillNode('orbCool1', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus1 = this.addNode(new DurationSkillNode('orbDur1', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 3
        ////////////////////////////
        
        cost++;

        const orbitRange2 = this.addNode(new NumberValueSkillNode('orbitRange2', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus2 = this.addNode(new NumberValueSkillNode('orbBonus2', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus2 = this.addNode(new CoolDownSkillNode('orbCool2', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus2 = this.addNode(new DurationSkillNode('orbDur2', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 4
        ////////////////////////////
        
        cost++;

        const orbitRange3 = this.addNode(new NumberValueSkillNode('orbitRange3', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));
    
        const orbBonus3 = this.addNode(new NumberValueSkillNode('orbBonus3', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus3 = this.addNode(new CoolDownSkillNode('orbCool3', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus3 = this.addNode(new DurationSkillNode('orbDur3', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        const addMinerNode2 = this.addNode(new AddMinerSkillNode('addOrbitMiner2', '+1 Orbit Miner', 'Orbiting Damage', 
            totals, Images.skill.orbit, new BigNum(cost + Settings.skill.costs.addMiner2), this, crew, SkillIds.ORBIT
        ));

        ////////////////////////////
        // Cost Tier 5
        ////////////////////////////
        
        cost++;

        const orbitRange4 = this.addNode(new NumberValueSkillNode('orbitRange4', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus4 = this.addNode(new NumberValueSkillNode('orbBonus4', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus4 = this.addNode(new CoolDownSkillNode('orbCool4', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus4 = this.addNode(new DurationSkillNode('orbDur4', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 6
        ////////////////////////////
        
        cost++;

        const orbitRange5 = this.addNode(new NumberValueSkillNode('orbitRange5', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus5 = this.addNode(new NumberValueSkillNode('orbBonus5', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus5 = this.addNode(new CoolDownSkillNode('orbCool5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus5 = this.addNode(new DurationSkillNode('orbDur5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 7
        ////////////////////////////
        
        cost++;

        const orbitRange6 = this.addNode(new NumberValueSkillNode('orbitRange6', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus6 = this.addNode(new NumberValueSkillNode('orbBonus6', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus6 = this.addNode(new CoolDownSkillNode('orbCool6', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus6 = this.addNode(new DurationSkillNode('orbDur6', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        const addMinerNode3 = this.addNode(new AddMinerSkillNode('addOrbitMiner3', '+1 Orbit Miner', 'Orbiting Damage', 
            totals, Images.skill.orbit, new BigNum(cost + Settings.skill.costs.addMiner3), this, crew, SkillIds.ORBIT
        ));

        ////////////////////////////
        // Cost Tier 8
        ////////////////////////////
        
        cost++;

        const orbitRange7 = this.addNode(new NumberValueSkillNode('orbitRange7', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus7 = this.addNode(new NumberValueSkillNode('orbBonus7', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus7 = this.addNode(new CoolDownSkillNode('orbCool7', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus7 = this.addNode(new DurationSkillNode('orbDur7', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 9
        ////////////////////////////
        
        cost++;

        const orbitRange8 = this.addNode(new NumberValueSkillNode('orbitRange8', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const orbBonus8 = this.addNode(new NumberValueSkillNode('orbBonus8', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const coolBonus8 = this.addNode(new CoolDownSkillNode('orbCool8', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const durationBonus8 = this.addNode(new DurationSkillNode('orbDur8', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        ////////////////////////////
        // Cost Tier 10
        ////////////////////////////

        cost++;

        const orbitRange9 = this.addNode(new NumberValueSkillNode('orbitRange9', '+1/2 Tile', 'Tile Range +0.5', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbitRangeUpgrades
        ));

        const coolBonus9 = this.addNode(new CoolDownSkillNode('orbCool9', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.coolDownUpgrades
        ));

        const orbBonus9 = this.addNode(new NumberValueSkillNode('orbBonus9', '+1 Orb', 'More Orbs',
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.orbMaxUpgrades
        ));

        const durationBonus9 = this.addNode(new DurationSkillNode('orbDur9', 
            totals, Images.skill.orbit, new BigNum(cost), this, 1, this.durationUpgrades
        ));

        const addMinerNode4 = this.addNode(new AddMinerSkillNode('addOrbitMiner4', '+1 Orbit Miner', 'Orbiting Damage', 
            totals, Images.skill.orbit, new BigNum(cost + Settings.skill.costs.addMiner4), this, crew, SkillIds.ORBIT
        ));


        ///////////////////////////////////
        // Setup node relationships
        ///////////////////////////////////

        addMinerNode1.addChildNode(orbBonus1);

        orbBonus1.addChildNode(orbBonus2);
        orbBonus2.addChildNode(orbBonus3);
        orbBonus3.addChildNode(orbBonus4);
        orbBonus4.addChildNode(orbBonus5);
        orbBonus5.addChildNode(orbBonus6);
        orbBonus6.addChildNode(orbBonus7);
        orbBonus7.addChildNode(orbBonus8);
        orbBonus8.addChildNode(orbBonus9);


        orbBonus1.addChildNode(coolBonus1);
        orbBonus2.addChildNode(coolBonus2);
        orbBonus3.addChildNode(coolBonus3);
        orbBonus4.addChildNode(coolBonus4);
        orbBonus5.addChildNode(coolBonus5);
        orbBonus6.addChildNode(coolBonus6);
        orbBonus7.addChildNode(coolBonus7);
        orbBonus8.addChildNode(coolBonus8);
        orbBonus9.addChildNode(coolBonus9);

        coolBonus1.addChildNode(durationBonus1);
        coolBonus2.addChildNode(durationBonus2);
        coolBonus3.addChildNode(durationBonus3);
        coolBonus4.addChildNode(durationBonus4);
        coolBonus5.addChildNode(durationBonus5);
        coolBonus6.addChildNode(durationBonus6);
        coolBonus7.addChildNode(durationBonus7);
        coolBonus8.addChildNode(durationBonus8);
        coolBonus9.addChildNode(durationBonus9);

        durationBonus1.addChildNode(orbitRange1);
        durationBonus2.addChildNode(orbitRange2);
        durationBonus3.addChildNode(orbitRange3);
        durationBonus4.addChildNode(orbitRange4);
        durationBonus5.addChildNode(orbitRange5);
        durationBonus6.addChildNode(orbitRange6);
        durationBonus7.addChildNode(orbitRange7);
        durationBonus8.addChildNode(orbitRange8);
        durationBonus9.addChildNode(orbitRange9);


        orbBonus3.addChildNode(addMinerNode2);
        orbBonus6.addChildNode(addMinerNode3);
        orbBonus9.addChildNode(addMinerNode4);

        ///////////////////////////////////
        // Position the nodes in the graph
        ///////////////////////////////////

        // Starting coordinate of the skill
        // const startX = 0;
        // const startY = -4;

        const startX = 3;
        const startY = 3;

        // unlockNode.setPosition(startX, startY);
        addMinerNode1.setPosition(startX, startY);


        orbBonus1.setPosition(startX + 1, startY);
        orbBonus2.setPosition(startX + 2, startY);
        orbBonus3.setPosition(startX + 3, startY);
        orbBonus4.setPosition(startX + 4, startY);
        orbBonus5.setPosition(startX + 5, startY);
        orbBonus6.setPosition(startX + 6, startY);
        orbBonus7.setPosition(startX + 7, startY);
        orbBonus8.setPosition(startX + 8, startY);
        orbBonus9.setPosition(startX + 9, startY);

        coolBonus1.setPosition(startX + 1, startY - 1);
        coolBonus2.setPosition(startX + 2, startY - 1);
        coolBonus3.setPosition(startX + 3, startY - 1);
        coolBonus4.setPosition(startX + 4, startY - 1);
        coolBonus5.setPosition(startX + 5, startY - 1);
        coolBonus6.setPosition(startX + 6, startY - 1);
        coolBonus7.setPosition(startX + 7, startY - 1);
        coolBonus8.setPosition(startX + 8, startY - 1);
        coolBonus9.setPosition(startX + 9, startY - 1);

        durationBonus1.setPosition(startX + 1, startY - 2);
        durationBonus2.setPosition(startX + 2, startY - 2);
        durationBonus3.setPosition(startX + 3, startY - 2);
        durationBonus4.setPosition(startX + 4, startY - 2);
        durationBonus5.setPosition(startX + 5, startY - 2);
        durationBonus6.setPosition(startX + 6, startY - 2);
        durationBonus7.setPosition(startX + 7, startY - 2);
        durationBonus8.setPosition(startX + 8, startY - 2);
        durationBonus9.setPosition(startX + 9, startY - 2);

        orbitRange1.setPosition(startX + 1, startY - 3);
        orbitRange2.setPosition(startX + 2, startY - 3);
        orbitRange3.setPosition(startX + 3, startY - 3);
        orbitRange4.setPosition(startX + 4, startY - 3);
        orbitRange5.setPosition(startX + 5, startY - 3);
        orbitRange6.setPosition(startX + 6, startY - 3);
        orbitRange7.setPosition(startX + 7, startY - 3);
        orbitRange8.setPosition(startX + 8, startY - 3);
        orbitRange9.setPosition(startX + 9, startY - 3);

        addMinerNode2.setPosition(startX + 3, startY + 1); // below orbBonus3
        addMinerNode3.setPosition(startX + 6, startY + 1); // below orbBonus6
        addMinerNode4.setPosition(startX + 9, startY + 1); // below orbBonus9
    }

    /**
     * @override
     * @param {PartyCharacter} miner
     * @param {number} frameTimeRatio 
     * @param {boolean} skillActive
     */
    updateSkillForFrame(miner, frameTimeRatio, skillActive) {

        /**
         * @type {Orbit}
         */
        let orbit = this.orbitByCharacter[miner.id];
        if (!orbit) {
            orbit = new Orbit(this.worldGrid, this.orbitRangeUpgrades, this.orbMaxUpgrades, this.tileMinedLogic);
            this.orbitByCharacter[miner.id] = orbit;
            this.orbitArray.push(orbit);
        }

        if (skillActive && !orbit.skillActive) {
            orbit.onSkillActivation();
        }
        else if (!skillActive && orbit.skillActive) {
            orbit.onSkillDeactivation();
        }

        if (skillActive) {
            orbit.updateForFrame(miner, frameTimeRatio);
        }
    }

    /**
     * @override
     * @returns {SkillRenderer}
     */
    getRenderer() {
        return this.renderer;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        super.resetFull();

        this.orbitArray.length = 0;
        this.orbitByCharacter = {};

        this.orbMaxUpgrades.reset();
        this.orbitRangeUpgrades.reset();
    }

}